import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FaUserCog,
    FaLink,
    FaBan,
    FaDollarSign,
    FaBrain,
    FaLock,
    FaChartLine,
    FaTimes,
    FaChevronDown,
    FaCheckCircle,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import fa from "./locales/FutureMedicine-fa.json";
import en from "./locales/FutureMedicine-en.json";
import ar from "./locales/FutureMedicine-ar.json";
import tr from "./locales/FutureMedicine-tr.json";

// ─── Feature Item ─────────────────────────────────────────────────────────────
const FeatureItem: React.FC<{ feature: string; index: number }> = ({ feature, index }) => {
    return (
        <motion.div
            className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-slate-200/70 shadow-xs hover:border-[#0072CE]/40 hover:bg-white transition-all duration-200"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 + 0.1, duration: 0.3 }}
        >
            <div className="w-2 h-2 rounded-full bg-[#0072CE] flex-shrink-0" />
            <span className="text-sm font-medium text-slate-700">
                {feature}
            </span>
        </motion.div>
    );
};

// ─── Problem Box ──────────────────────────────────────────────────────────────
const ProblemBox: React.FC<{ label: string; description: string }> = ({ label, description }) => {
    return (
        <div className="rounded-xl p-4 mb-3 bg-red-50/70 border-r-4 border-red-500/70 backdrop-blur-xs transition-colors duration-200 hover:bg-red-50/90">
            <p className="text-slate-700 text-sm leading-relaxed">
                <span className="font-bold text-red-600 inline-block mr-1">
                    {label}
                </span>
                {description}
            </p>
        </div>
    );
};

// ─── Solution Box ─────────────────────────────────────────────────────────────
const SolutionBox: React.FC<{ label: string; solution: string }> = ({ label, solution }) => {
    return (
        <div className="rounded-xl p-4 bg-blue-50/70 border-r-4 border-[#0072CE] backdrop-blur-xs transition-colors duration-200 hover:bg-blue-50/90 flex items-start justify-between gap-3">
            <div>
                <span className="font-bold text-sm block mb-1 text-[#0072CE]">
                    {label}
                </span>
                <p className="text-sm leading-relaxed font-medium text-slate-700">
                    {solution}
                </p>
            </div>
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5 text-[#0072CE]">
                <FaCheckCircle className="text-xs" />
            </div>
        </div>
    );
};

// ─── Feature Card ─────────────────────────────────────────────────────────────
interface CardData {
    id: number;
    title: string;
    description: string;
    solution: string;
    features: string[];
    icon: React.ReactNode;
}

const FeatureCard: React.FC<{
    card: CardData;
    index: number;
    expandedCard: number | null;
    onToggle: (i: number) => void;
    t: any;
}> = ({ card, index, expandedCard, onToggle, t }) => {
    const isExpanded = expandedCard === index;
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div
            layout
            ref={cardRef}
            className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                isExpanded ? "bg-white/95" : "bg-white/80"
            } backdrop-blur-md`}
            style={{
                border: isExpanded
                    ? "1.5px solid rgba(0, 114, 206, 0.45)"
                    : isHovered
                    ? "1.5px solid rgba(0, 114, 206, 0.3)"
                    : "1.5px solid rgba(226, 232, 240, 0.85)",
                boxShadow: isExpanded
                    ? "0 20px 40px -10px rgba(0, 114, 206, 0.15), 0 4px 16px rgba(0,0,0,0.03)"
                    : isHovered
                    ? "0 16px 36px -8px rgba(0, 114, 206, 0.12), 0 4px 12px rgba(0,0,0,0.02)"
                    : "0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -1px rgba(15, 23, 42, 0.02)",
            }}
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.08, type: "spring", stiffness: 200, damping: 22 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => onToggle(index)}
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Dynamic Spotlight Glow */}
            <div
                className="pointer-events-none absolute -inset-px transition-opacity duration-300 rounded-2xl"
                style={{
                    opacity: isHovered || isExpanded ? 1 : 0,
                    background: `radial-gradient(450px circle at ${mousePos.x}px ${mousePos.y}px, rgba(0, 114, 206, 0.1), transparent 70%)`,
                }}
            />

            {/* Subtle top accent line */}
            <div
                className={`absolute top-0 inset-x-0 h-[2px] transition-all duration-500 origin-center ${
                    isExpanded ? "scale-x-100 opacity-100" : "scale-x-0 group-hover:scale-x-100 opacity-80"
                }`}
                style={{
                    background: "linear-gradient(90deg, transparent, #0072CE, transparent)",
                }}
            />

            <div className="relative z-10 p-5 md:p-6">
                {/* Header */}
                <div className="flex items-center gap-3.5 mb-5">
                    <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                            isExpanded || isHovered
                                ? "bg-blue-500/15 text-[#0072CE] scale-105 border border-blue-200"
                                : "bg-slate-100 text-slate-600 border border-slate-200/60"
                        }`}
                    >
                        <span className="text-xl">{card.icon}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                        <h4
                            className={`text-base md:text-lg font-bold leading-tight transition-colors duration-300 ${
                                isExpanded || isHovered ? "text-[#0072CE]" : "text-slate-900"
                            }`}
                        >
                            {card.title}
                        </h4>
                    </div>

                    <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                            isExpanded
                                ? "bg-[#0072CE] text-white shadow-sm rotate-180"
                                : "bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-[#0072CE]"
                        }`}
                    >
                        <FaChevronDown className="text-xs transition-transform duration-300" />
                    </div>
                </div>

                <ProblemBox label={t("labels.problem", { ns: "FutureMedicine" })} description={card.description} />
                <SolutionBox label={t("labels.solutionTitle", { ns: "FutureMedicine" })} solution={card.solution} />

                {/* Expandable features */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                            className="overflow-hidden"
                        >
                            <div className="mt-5 pt-5 border-t border-slate-200/80">
                                <div className="flex items-center gap-2 mb-3.5">
                                    <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center text-[#0072CE]">
                                        <FaChartLine className="text-xs" />
                                    </div>
                                    <span className="text-[#0072CE] font-bold text-sm">
                                        {t("labels.keyFeatures", { ns: "FutureMedicine" })}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    {card.features.map((feature, i) => (
                                        <FeatureItem key={i} feature={feature} index={i} />
                                    ))}
                                </div>

                                <div className="flex justify-center mt-5">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggle(index);
                                        }}
                                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-[#0072CE] text-sm font-medium border border-slate-200 hover:border-blue-200 transition-all duration-200 cursor-pointer"
                                    >
                                        <span>{t("labels.close", { ns: "FutureMedicine" })}</span>
                                        <FaTimes className="text-xs" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Expand hint */}
                {!isExpanded && (
                    <div className="flex justify-center mt-4 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 group-hover:text-[#0072CE] font-medium transition-colors duration-200">
                            <span>{t("labels.clickToExpand", { ns: "FutureMedicine" })}</span>
                            <FaChevronDown className="text-[10px] transform group-hover:translate-y-0.5 transition-transform duration-200" />
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

// ─── Toggle Button (Show More / Show Less) ────────────────────────────────────
const ToggleCardsButton: React.FC<{
    showAll: boolean;
    onToggle: () => void;
    showMoreLabel: string;
    showLessLabel: string;
}> = ({ showAll, onToggle, showMoreLabel, showLessLabel }) => {
    return (
        <button
            type="button"
            onClick={onToggle}
            className={`group relative flex items-center gap-3 px-8 py-3.5 rounded-2xl font-bold text-sm sm:text-base text-white transition-all duration-300 cursor-pointer shadow-md hover:shadow-xl active:scale-98 ${
                showAll
                    ? "bg-slate-700 hover:bg-slate-800 border border-slate-600"
                    : "bg-[#0072CE] hover:bg-blue-700 border border-blue-400/40"
            }`}
        >
            <AnimatePresence mode="wait">
                <motion.span
                    key={showAll ? "less" : "more"}
                    initial={{ opacity: 0, y: showAll ? -4 : 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: showAll ? 4 : -4 }}
                    transition={{ duration: 0.2 }}
                >
                    {showAll ? showLessLabel : showMoreLabel}
                </motion.span>
            </AnimatePresence>

            <FaChevronDown
                className={`text-xs transition-transform duration-300 ${
                    showAll ? "rotate-180" : "group-hover:translate-y-0.5"
                }`}
            />
        </button>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FutureMedicine: React.FC = () => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const ns = "FutureMedicine";
        const resources = { fa, en, ar, tr } as const;
        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) i18n.addResourceBundle(lng, ns, data, true, true);
        });
    }, [i18n]);

    const [expandedCard, setExpandedCard] = useState<number | null>(null);
    const [showAllCards, setShowAllCards] = useState(false);

    const toggleCardExpansion = (index: number) => {
        setExpandedCard(expandedCard === index ? null : index);
    };

    const iconList: React.ReactNode[] = [
        <FaUserCog key="1" className="text-xl" />,
        <FaLink key="2" className="text-xl" />,
        <FaBan key="3" className="text-xl" />,
        <FaDollarSign key="4" className="text-xl" />,
        <FaBrain key="5" className="text-xl" />,
        <FaLock key="6" className="text-xl" />,
    ];

    const cards: CardData[] = Array.from({ length: 6 }, (_, i) => ({
        id: i + 1,
        title: t(`cards.${i}.title`, { ns: "FutureMedicine" }),
        description: t(`cards.${i}.description`, { ns: "FutureMedicine" }),
        solution: t(`cards.${i}.solution`, { ns: "FutureMedicine" }),
        features: t(`cards.${i}.features`, { ns: "FutureMedicine", returnObjects: true }) as string[],
        icon: iconList[i],
    }));

    const displayedCards = showAllCards ? cards : cards.slice(0, 2);

    const handleToggleShowAll = () => {
        if (showAllCards) {
            setExpandedCard(null);
        }
        setShowAllCards((prev) => !prev);
    };

    return (
        <div className="w-full text-gray-800 relative overflow-hidden">
            {/* Ambient subtle light gradient */}
            <div
                className="absolute pointer-events-none w-96 h-96 rounded-full bg-blue-400/5 blur-3xl -top-10 -right-10"
            />
            <div
                className="absolute pointer-events-none w-80 h-80 rounded-full bg-sky-400/5 blur-3xl -bottom-10 -left-10"
            />

            <div className="relative z-10 pb-12 container mx-auto">
                {/* Header */}
                <motion.div
                    className="flex"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                >
                    <div className="flex md:w-4/6 mb-8 md:mb-12 flex-col gap-4 px-2 md:px-6 relative z-10">
                        <div className="hidden md:block text-base font-semibold text-[#0072CE]">
                            {t("header.tag", { ns: "FutureMedicine" })}
                        </div>

                        <div className="flex flex-col gap-1">
                            <div className="flex items-start gap-2 flex-wrap">
                                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
                                    {t("header.h1Prefix", { ns: "FutureMedicine" })}
                                </h2>
                                <h2 className="text-3xl md:text-4xl font-bold text-[#0072CE]">
                                    {t("header.h1Highlight", { ns: "FutureMedicine" })}
                                </h2>
                            </div>
                            <h3 className="text-3xl md:text-4xl font-normal text-neutral-900">
                                {t("header.h2", { ns: "FutureMedicine" })}
                            </h3>
                        </div>

                        <p className="text-sm text-neutral-600 leading-[190%]">
                            {t("header.description", { ns: "FutureMedicine" })}
                        </p>
                    </div>
                </motion.div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-2">
                    <AnimatePresence mode="popLayout">
                        {displayedCards.map((card, index) => (
                            <FeatureCard
                                key={card.id}
                                card={card}
                                index={index}
                                expandedCard={expandedCard}
                                onToggle={toggleCardExpansion}
                                t={t}
                            />
                        ))}
                    </AnimatePresence>
                </div>

                {/* Show More / Show Less Button */}
                {cards.length > 2 && (
                    <motion.div
                        className="flex justify-center mt-8 px-2"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        viewport={{ once: true }}
                    >
                        <ToggleCardsButton
                            showAll={showAllCards}
                            onToggle={handleToggleShowAll}
                            showMoreLabel={t("labels.showMore", { ns: "FutureMedicine" })}
                            showLessLabel={t("labels.showLess", { ns: "FutureMedicine" })}
                        />
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default FutureMedicine;
